import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractComponent,
  Channel,
  ChannelHealthService,
  ChannelQualityResponse,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs';

/**
 * "Qualidade e Entrega" area (SPEC-2026-08-channel-health, T-23 — RF-27…RF-30).
 * Current band only (no history — decision 2026-09-02), four delivery KPIs,
 * templates below High ordered by sent volume, opt-out counter with 30d delta.
 */
@Component({
    selector: 'health-quality-tab',
    templateUrl: './health-quality-tab.component.html',
    styleUrl: './health-quality-tab.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HealthQualityTabComponent extends AbstractComponent implements OnInit, OnChanges {

  @Input({ required: true }) channel: Channel;
  @Input() refreshTick: number = 0;

  quality?: ChannelQualityResponse;

  constructor(
    public injector: Injector,
    private channelHealthService: ChannelHealthService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshTick'] && !changes['refreshTick'].firstChange) {
      this.load();
    }
  }

  private load(): void {
    this.loadingContent = true;
    this.channelHealthService.getQuality(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (quality) => {
          this.quality = quality;
          this.loadingContent = false;
        },
        error: (err) => {
          this.loadingContent = false;
          this.alertService.error(err?.error?.message);
        },
      });
  }

  // ---------------------------------------------------------------- band (RF-27)

  band(): string {
    return (this.quality?.quality?.band ?? '').toUpperCase();
  }

  bandLabel(): string {
    const map: Record<string, string> = {
      GREEN: 'channel.health.quality.high',
      YELLOW: 'channel.health.quality.medium',
      RED: 'channel.health.quality.low',
    };
    return map[this.band()] ? this.t(map[this.band()]) : this.t('channel.health.quality.unknown');
  }

  /** System Bootstrap alert variant for the current band block. */
  bandAlertClass(): string {
    if (this.band() === 'GREEN') return 'alert-success';
    if (this.band() === 'YELLOW') return 'alert-warning';
    if (this.band() === 'RED') return 'alert-danger';
    return 'alert-secondary';
  }

  bandText(): string {
    const map: Record<string, string> = {
      GREEN: 'channel.health.qualityTab.bandTextHigh',
      YELLOW: 'channel.health.qualityTab.bandTextMedium',
      RED: 'channel.health.qualityTab.bandTextLow',
    };
    return this.t(map[this.band()] ?? 'channel.health.qualityTab.bandTextUnknown');
  }

  // ---------------------------------------------------------------- KPIs (RF-28)

  percentLabel(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return `${(value * 100).toFixed(1).replace('.', ',')}%`;
  }

  numberLabel(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString('pt-BR');
  }

  costLabel(): string | null {
    const cost = this.quality?.kpis?.billedCost30d;
    if (cost === null || cost === undefined) return null;
    return cost.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' });
  }

  // ---------------------------------------------------------------- templates (RF-29)

  templateQualityClass(quality: string): string {
    if (quality === 'YELLOW') return 'is-warn';
    if (quality === 'RED') return 'is-err';
    return 'is-neutral';
  }

  templateQualityLabel(quality: string): string {
    if (quality === 'YELLOW') return this.t('channel.health.quality.medium');
    if (quality === 'RED') return this.t('channel.health.quality.low');
    return quality;
  }

  // ---------------------------------------------------------------- opt-outs (RF-30)

  optOutDeltaLabel(): string {
    const delta = this.quality?.optOuts?.delta ?? 0;
    if (delta > 0) return this.t('channel.health.qualityTab.optOutsUp', { delta });
    if (delta < 0) return this.t('channel.health.qualityTab.optOutsDown', { delta: Math.abs(delta) });
    return this.t('channel.health.qualityTab.optOutsSame');
  }

  optOutDeltaClass(): string {
    const delta = this.quality?.optOuts?.delta ?? 0;
    if (delta > 0) return 'text-danger';
    if (delta < 0) return 'text-success';
    return 'text-muted';
  }
}
